import { useState } from "react";
import { PATH, states } from "utils/const";
import { Spinner } from "react-bootstrap";
import { verifyBillingAddress } from "services/post-verifyBillingAddress";
import { useRecoilState, useSetRecoilState } from "recoil";
import { cartState, selectedAddressTypeState, showChangeBillingAddressModalState, toastState } from "atoms";
import { CheckoutFormModel } from "atoms/models";
import { extractPoBoxNumberFromSelectedAddress, getCityFromSelectedAddress, getStateFromSelectedAddress, getZipCodeFromSelectedAddress } from "utils";
import { useLocation } from "react-router-dom";

const ChangePOBox: React.FC<{ setCheckoutForm: Function; checkoutForm: CheckoutFormModel }> = ({ setCheckoutForm, checkoutForm }) => {
  const [spinner, setSpinner] = useState(false);
  const setShowChangeBillingAddressModal = useSetRecoilState(showChangeBillingAddressModalState);
  const [buttonText, setButtonText] = useState("Submit");
  const [isInvalidAddress, setIsInvalidAddress] = useState(false);
  const [cart, setCart] = useRecoilState(cartState);
  const setSelectedAddressType = useSetRecoilState(selectedAddressTypeState);
  const [toast, setToast] = useRecoilState(toastState);
  const location = useLocation();
  const [poBoxAddress, setPoBoxAddress] = useState({
    poBox: "",
    city: "",
    state: "",
    stateAbbriviation: "",
    zipCode: "",
  });
  const [alert, setAlert] = useState({
    alertShow: false,
    alertMessage: "",
  });

  const resetState = () => {
    setAlert({ ...alert, alertMessage: "", alertShow: false });
    setPoBoxAddress({ ...poBoxAddress, poBox: "", city: "", state: "", stateAbbriviation: "", zipCode: "" });
    setShowChangeBillingAddressModal(false);
    setButtonText("Submit");
    setIsInvalidAddress(false);
    setSelectedAddressType("");
  };

  const handlePOBoxSubmit = async () => {
    setSpinner(true);
    if (!poBoxAddress.poBox || !poBoxAddress.city || !poBoxAddress.state || !poBoxAddress.zipCode || poBoxAddress.zipCode.length < 5) {
      setAlert({ ...alert, alertShow: true, alertMessage: "You must enter a complete PO Box address before submitting" });
      setSpinner(false);
      return;
    } else {
      const selectedAddress = {
        addressNumber: `${poBoxAddress.poBox}`,
        formattedAddress: `Po Box ${poBoxAddress.poBox}, ${poBoxAddress.city}, ${poBoxAddress.state} ${poBoxAddress.zipCode}`,
        formattedLocationAddress: `${poBoxAddress.city}, ${poBoxAddress.state} ${poBoxAddress.zipCode}`,
        formattedStreetAddress: `Po Box ${poBoxAddress.poBox}`,
      };

      const response: any = await verifyBillingAddress(selectedAddress, "");
      if (response.status === "success") {
        setCart({
          ...cart,
          billingStreetNumber: " ",
          billingStreetName: " ",
          billingCity: response?.addressVerificationInput?.city,
          billingState: response.addressVerificationInput.state,
          billingZipCode: response.addressVerificationInput.postalCode,
          billingApartmentNumber: "",
          billingPOBox: poBoxAddress.poBox,
        });

        setCheckoutForm({
          ...checkoutForm,
          billingStreetNumber: " ",
          billingStreetName: " ",
          billingCity: response.addressVerificationInput.city,
          billingState: response.addressVerificationInput.state,
          billingZipCode: response.addressVerificationInput.postalCode,
          billingApartmentNumber: "",
          billingPOBox: poBoxAddress.poBox,
        });

        setSpinner(false);
        resetState();
        setToast({
          ...toast,
          show: true,
          variant: "success",
          header: "Success",
          message:
            location.pathname === PATH.CANCEL
              ? "Your PO Box has been updated. All future bills will be sent to your new address."
              : "Your PO Box has been updated.",
        });
      } else {
        setAlert({
          ...alert,
          alertShow: true,
          alertMessage: "Your address could not be verified with USPS. Please make sure your address is correct before continuing.",
        });
        setButtonText("This address is valid. Please continue");
        setIsInvalidAddress(true);
        setSpinner(false);
        return;
      }
    }
  };

  const handlePOBoxOverride = async () => {
    const selectedAddress = {
      addressNumber: `${poBoxAddress.poBox}`,
      formattedAddress: `Po Box ${poBoxAddress.poBox}, ${poBoxAddress.city}, ${poBoxAddress.state} ${poBoxAddress.zipCode}`,
      formattedLocationAddress: `${poBoxAddress.city}, ${poBoxAddress.state} ${poBoxAddress.zipCode}`,
      formattedStreetAddress: `Po Box ${poBoxAddress.poBox}`,
    };
    setSpinner(true);
    const response: any = await verifyBillingAddress(selectedAddress, "");
    let streetNumber: any;
    let streetName: any;
    let poBox: any;
    let city: any;
    let state: any;
    let zipCode: any;

    if (response.status !== "success") {
      if (selectedAddress.formattedAddress.includes("Po Box")) {
        streetNumber = " ";
        streetName = `PO Box ${selectedAddress.addressNumber}`;
        poBox = extractPoBoxNumberFromSelectedAddress(selectedAddress.formattedAddress);
        city = getCityFromSelectedAddress(selectedAddress.formattedLocationAddress);
        state = getStateFromSelectedAddress(selectedAddress.formattedLocationAddress);
        zipCode = getZipCodeFromSelectedAddress(selectedAddress.formattedLocationAddress);
      } else {
        city = response.addressVerificationInput.city;
        state = response.addressVerificationInput.state;
        zipCode = response.addressVerificationInput.postalCode;
      }
    } else {
      city = response.addressVerificationInput.city;
      state = response.addressVerificationInput.state;
      zipCode = response.addressVerificationInput.postalCode;
    }

    setSpinner(false);
    setCart({
      ...cart,
      billingStreetNumber: streetNumber,
      billingStreetName: streetName,
      billingCity: city,
      billingState: state,
      billingZipCode: zipCode,
      billingApartmentNumber: "",
      billingPOBox: poBox,
    });

    setCheckoutForm({
      ...checkoutForm,
      billingStreetNumber: streetNumber,
      billingStreetName: streetName,
      billingCity: city,
      billingState: state,
      billingZipCode: zipCode,
      billingApartmentNumber: "",
      billingPOBox: poBox,
    });
    resetState();
    setToast({
      ...toast,
      show: true,
      variant: "success",
      header: "Success",
      message:
        location.pathname === PATH.CANCEL
          ? "Your PO Box has been updated. All future bills will be sent to your new address."
          : "Your PO Box has been updated.",
    });
  };

  return (
    <div className="changepo-box-container">
      {alert.alertShow ? (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {alert.alertMessage}
          <button
            type="button"
            className="btn-close"
            data-bs-dismiss="alert"
            aria-label="Close"
            onClick={() => setAlert({ ...alert, alertShow: false, alertMessage: "" })}
          ></button>
        </div>
      ) : null}
      <div className="input-group has-validation">
        <div className="form-floating">
          <input
            type="text"
            className="form-control"
            id="poBox"
            placeholder="P.O. Box"
            inputMode="numeric"
            value={poBoxAddress.poBox}
            onChange={(e) => {
              // Remove all non-numeric characters
              const newValue = e.target.value.replace(/[^0-9]/g, "");
              setPoBoxAddress({ ...poBoxAddress, poBox: newValue });
            }}
            onFocus={() => setAlert({ ...alert, alertMessage: "", alertShow: false })}
          />
          <label htmlFor="poBox">P.O. Box</label>
        </div>
      </div>
      <div className="input-group has-validation mt-2">
        <div className="form-floating me-2">
          <input
            type="text"
            className="form-control"
            id="city"
            placeholder="City"
            value={poBoxAddress.city}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPoBoxAddress({ ...poBoxAddress, city: e.target.value })}
            onFocus={() => setAlert({ ...alert, alertMessage: "", alertShow: false })}
          />
          <label htmlFor="city">City</label>
        </div>
        <div className="form-floating me-2">
          <select
            className="form-select"
            id="state"
            value={poBoxAddress.state}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setPoBoxAddress({ ...poBoxAddress, state: e.target.value })}
            onFocus={() => setAlert({ ...alert, alertMessage: "", alertShow: false })}
          >
            <option selected></option>
            {states.map((state: any) => {
              return (
                <option key={state.name} value={state.abbreviation}>
                  {state.name}
                </option>
              );
            })}
          </select>
          <label htmlFor="state">State</label>
        </div>
        <div className="form-floating">
          <input
            type="text"
            className="form-control"
            id="zipCode"
            placeholder="Zip Code"
            inputMode="numeric"
            value={poBoxAddress.zipCode}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value;
              // Regular expression to keep only numeric values
              if (/^\d*$/.test(value)) {
                setPoBoxAddress({ ...poBoxAddress, zipCode: value });
              }
            }}
            onFocus={() => setAlert({ ...alert, alertMessage: "", alertShow: false })}
            maxLength={5}
          />
          <label htmlFor="zipCode">Zip Code</label>
        </div>
      </div>
      <div className="text-end mt-3">
        {spinner ? <Spinner animation="border" className="billing-address-spinner me-2" style={{ marginBottom: "-10px" }} /> : null}
        <button className="btn btn-primary me-2" onClick={() => resetState()}>
          Cancel
        </button>
        <button className="btn btn-primary" onClick={() => (isInvalidAddress ? handlePOBoxOverride() : handlePOBoxSubmit())}>
          {buttonText}
        </button>
      </div>
    </div>
  );
};

export default ChangePOBox;
